<template>
  <div>
    <div class="mb-3">
          <h3>{{ $t("admin.global_announcements") }}</h3>
          <p class="text-terciary m-1">{{ $t("admin.global_announcements_help") }}</p>
    </div>
    <b-row>
      <b-col cols="12" lg="8" xl="6">
        <b-card class="shadow-sm rounded-xl">
          <AdminCommunicationComposer
            :title="$t('admin.global_announcement_new')"
            :hint="$t('admin.global_announcement_hint')"
            :placeholder="$t('admin.global_announcement_placeholder')"
            :publishLabel="$t('admin.publish')"
            :maxLength="500"
            @submit="createGlobalAnnouncement"
          />
          <!-- Root admin only: publish to all units in this organization -->
          <div v-if="canPostToAll" class="mt-2" >
          <b-form-checkbox v-model="postToAll" class="visible-checkbox">
              {{ $t("admin.send_to_all_units") }}
          </b-form-checkbox>
          <small class="text-muted d-block">
              {{ $t("admin.send_to_all_units_help") }}
          </small>
          </div>      

          <div class="d-flex align-items-center justify-content-between mt-4 mb-2">
            <h4 class="text-terciary m-0">{{ $t("admin.latest_global_announcements") }}</h4>
          </div>

          <b-overlay :show="loading">
            <div v-if="items.length">
              <div v-for="item in items" :key="item.id" class="mb-4">
                <div class="d-flex align-items-start">
                  <b-img
                    class="mr-2 rounded-circle"
                    style="object-fit: cover;"
                    height="40"
                    width="40"
                    v-if="item.userprofile.avatar_file_path && item.userprofile.avatar_file_name"
                    :src="`${item.userprofile.avatar_file_path}${item.userprofile.avatar_file_name}`"
                    alt="img"
                  />
                  <b-avatar
                    v-else
                    style="background:#E63375"
                    class="mr-2"
                    :text="item.userprofile.initials || 'A'"
                    size="40"
                  />

                  <div class="w-100">
                    <div class="d-flex align-items-center">
                      <span class="text-terciary font-weight-semibold mr-2">
                        {{ item.userprofile.full_name || $t("admin.admin_user") }}
                      </span>
                      <span class="text-muted small">
                        <b-icon-clock class="mr-1" />
                        {{ item.created_dt }}
                      </span>

                      <b-button
                        class="m-0 p-0 ml-auto btn shadow-none"
                        style="background:transparent;border:none"
                        @click="confirmDelete(item)"
                        :disabled="deletingId === item.id"
                      >
                        <b-icon-trash-fill class="text-danger" />
                      </b-button>
                    </div>

                    <div v-if="item.mediafile" class="mt-2">
                      <b-img-lazy
                        v-if="item.mediafile.media_type_cd === 'IMG'"
                        style="object-fit: cover;width:100%;"
                        :src="`${item.mediafile.file_path}${item.mediafile.file_name}`"
                        alt="img"
                      />
                      <video
                        v-else-if="item.mediafile.media_type_cd === 'MOV'"
                        :src="`${item.mediafile.file_path}${item.mediafile.file_name}`"
                        width="100%"
                        controls
                      />
                    </div>

                    <p class="text-terciary font-weight-normal mt-2 mb-0" style="white-space: pre-line;">
                      {{ item.text }}
                    </p>
                    <!-- Reactions -->
                    <div
                    class="d-flex align-items-center mt-2"
                    v-if="item.reactions && item.reactions.length"
                    >
                        <!-- Reactions summary (hover shows who reacted) -->
                        <div
                        class="d-flex align-items-center mt-2"
                        v-if="item.reactions && item.reactions.length"
                        >
                        <div
                            v-for="reaction in item.reactions"
                            :key="reaction.reaction_id"
                            class="mr-1 py-1 pl-1 d-flex justify-content-center align-items-center reaction-pill"
                        >
                            <b-button
                            class="m-0 p-0 btn shadow-none"
                            style="background:transparent;border:none"
                            :id="`${item.id}-${reaction.reaction_id}`"
                            >
                            <span class="emoji-size">{{ reaction.reaction }}</span>
                            </b-button>

                            <b-popover
                            :target="`${item.id}-${reaction.reaction_id}`"
                            triggers="hover"
                            placement="bottomright"
                            variant="primary"
                            >
                            <template #title>
                                {{ $t("admin.reacted_users") }}
                            </template>
                            <Tooltip :communication_id="item.id" :reaction_id="reaction.reaction_id" />
                            </b-popover>

                            <span class="text-terciary font-weight-normal mr-2 emoji-count">
                            {{ reaction.reactions_count }}
                            </span>
                        </div>
                        </div>

                    </div>                
                  </div>
                </div>

                <div class="divider mt-3"></div>
              </div>

              <div v-if="links.more_pages" v-observe-visibility="onBottom"></div>
            </div>

            <div v-else class="text-center py-4">
              <h6 class="text-primary">{{ $t("admin.no_global_announcements") }}</h6>
            </div>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>    

    <b-modal
      id="admin-delete-global-comm"
      ref="deleteModal"
      centered
      hide-footer
      title-class="text-terciary"
      :title="$t('admin.delete_announcement_title')"
    >
      <p class="text-terciary mb-3">{{ $t("admin.delete_announcement_confirm") }}</p>

      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="rounded mr-2" @click="hideDeleteModal">
          {{ $t("cancel") }}
        </b-button>
        <b-button variant="danger" class="rounded" :disabled="!deleteTarget || deletingId" @click="deleteCommunication">
          <b-spinner small v-if="deletingId" class="mr-2" />
          {{ $t("delete") }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios"
import AdminCommunicationComposer from "@/components/Admin/AdminCommunicationComposer.vue"
import Tooltip from "@/components/Checkout_Team/Tooltip.vue"
import { BIconTrashFill, BIconClock} from "bootstrap-vue"

export default {
  name: "GlobalAnnouncementsTab",
  components: { AdminCommunicationComposer, Tooltip, BIconTrashFill, BIconClock},
  props: {
  },
  data() {
    return {
      loading: false,
      items: [],
      links: null,
      deleteTarget: null,
      deletingId: null,
      postToAll: false,
      canPostToAll: false,
    }
  },
  methods: {
    async fetchCommunications(pageURL, isPaginated = false) {
      this.loading = true      
      const url = pageURL || "/admin/getadmincommunications"

      try {
        const response = await axios.get(url)
        if (response.status === 200) {
          const data = response.data.data || []
          this.items = isPaginated ? [...this.items, ...data] : data
          this.links = response.data.links ?? null

          // Backend should return this for root admins
          this.canPostToAll = !!response.data.can_post_to_all
          if (!this.canPostToAll) this.postToAll = false          
        }
      } catch ({ response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response.data.error_message || this.$t("error_occurred"),
        })
      } finally {
        this.loading = false
      }
    },

    async createGlobalAnnouncement({ text, media_file_id }) {
      const payload = {
        communication_text: text,
        post_to_all: this.postToAll ? 1 : 0,
      }
      if (media_file_id) payload.media_file_id = media_file_id

      try {
        const response = await axios.post("/admin/saveadmincommunication", payload)
        if (response.status === 200) {
          this.$store.dispatch("addToastMessage", {
            type: "success",
            text: this.$t("admin.announcement_published"),
          })
          await this.fetchCommunications()
          return
        }

        throw new Error("Unexpected response")
      } catch ({ response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response.data.error_message || this.$t("error_occurred"),
        })
        // Throw so composer doesn't reset
        throw new Error("Create failed")
      }
    },

    onBottom(isVisible) {
      if (!isVisible) return
      if (this.links.more_pages) this.fetchCommunications(this.links.next_page_url, true)
    },

    confirmDelete(item) {
      this.deleteTarget = item
      this.$refs.deleteModal.show()
    },

    hideDeleteModal() {
      this.deleteTarget = null
      this.$refs.deleteModal.hide()
    },

    async deleteCommunication() {
      if (!this.deleteTarget) return
      const id = this.deleteTarget.id
      this.deletingId = id

      try {
        const response = await axios.delete("/communication", {
          params: { communication_id: id },
        })

        if (response.status === 204) {
          this.items = this.items.filter(x => x.id !== id)
          this.$store.dispatch("addToastMessage", {
            type: "success",
            text: this.$t("admin.announcement_deleted"),
          })
          this.hideDeleteModal()
        }
      } catch ({ response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response.data.error_message || this.$t("error_occurred"),
        })
      } finally {
        this.deletingId = null
      }
    },
  },
  created() {
    this.fetchCommunications()
  },
}
</script>

<style scoped>
.divider {
  border-bottom: 1px solid #d2d2d2;
}

.reaction-pill {
  background: #eeeeee;
  border-radius: 50%;
}

.emoji-size {
  font-size: 14px;
}

@media (min-width: 550px) {
  .emoji-size {
    font-size: 16px;
  }
}

</style>
